import type { PlatformAuditEventDto } from '@fruuit/shared'
import {
  Badge,
  Button,
  Callout,
  type Column,
  DataTable,
  DateInput,
  Drawer,
  PageHeader,
  Select,
} from '@fruuit/ui'
import { ShieldAlert, ShieldCheck } from 'lucide-react'
import { useState } from 'react'
import { errorMessage, isApiError } from '../../api/client'
import { useAdminAudit, useAuditVerify, useCursorPager } from '../../features/admin-users/api'
import { formatDateTime } from '../../features/auth/format'
import { MfaRequiredNotice } from '../../features/auth/StepUp'
import { useTitle } from '../../lib/useTitle'

const ACTION_FILTERS = [
  { value: 'auth.', label: 'Sign-ins, sign-outs and denials' },
  { value: 'auth.denied', label: 'Denied sign-ins' },
  { value: 'impersonation.', label: 'Impersonation' },
  { value: 'admission.', label: 'Admission changes' },
  { value: 'user.', label: 'User changes' },
  { value: 'invite.', label: 'Invitations' },
  { value: 'membership.', label: 'Memberships' },
  { value: 'staff.', label: 'Staff roles' },
  { value: 'operator.', label: 'Operator grants' },
  { value: 'org.', label: 'Organisations' },
  { value: 'mfa.', label: 'Two-factor' },
]

const ACTION_LABELS: Record<string, string> = {
  'auth.sign_in': 'Signed in',
  'auth.sign_out': 'Signed out',
  'auth.sign_out_all': 'Signed out everywhere',
  'auth.denied': 'Sign-in denied',
  'auth.session_revoke': 'Ended a session',
  'impersonation.start': 'Started viewing as',
  'impersonation.end': 'Stopped viewing as',
  'admission.platform_role': 'Platform role changed',
  'identity.link': 'Linked a sign-in identity',
  'invite.create': 'Invited',
  'invite.resend': 'Resent invitation',
  'invite.revoke': 'Revoked invitation',
  'invite.accept': 'Accepted invitation',
  'membership.grant': 'Granted access',
  'membership.provision': 'Provisioned staff roles',
  'membership.role_change': 'Changed role',
  'staff.roles': 'Changed staff roles',
  'operator.grant': 'Granted operator',
  'operator.revoke': 'Removed operator',
  'org.create': 'Created organisation',
  'org.update': 'Edited organisation',
  'org.suspend': 'Suspended organisation',
  'org.resume': 'Resumed organisation',
  'user.create': 'Created user',
  'user.activate': 'Activated user',
  'user.deactivate': 'Deactivated user',
  'user.reactivate': 'Reactivated user',
  'user.sessions_revoke': 'Signed user out everywhere',
  'user.mfa_reset': 'Reset two-factor',
  'user.profile_update': 'Updated profile',
  'mfa.enrol': 'Set up two-factor',
  'mfa.step_up': 'Verified second factor',
  'mfa.recovery_code_used': 'Used a recovery code',
  'mfa.disable': 'Turned off two-factor',
}

function summary(event: PlatformAuditEventDto): string {
  const d = (event.details ?? {}) as Record<string, unknown>
  const parts: string[] = []
  for (const key of ['email', 'target_email', 'reason', 'provider', 'role']) {
    const value = d[key]
    if (typeof value === 'string' && value) parts.push(key === 'reason' ? `“${value}”` : value)
  }
  return parts.join(' · ')
}

/** Admin → Audit (P-ADM-013): the hash-chained platform log, newest first. */
export function AuditPage() {
  useTitle('Platform audit')
  const [action, setAction] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const pager = useCursorPager()
  const audit = useAdminAudit({ action, from, to, limit: 50, cursor: pager.cursor })
  const verify = useAuditVerify()
  const [open, setOpen] = useState<PlatformAuditEventDto | null>(null)

  const columns: Column<PlatformAuditEventDto>[] = [
    {
      id: 'ts',
      header: 'When',
      cell: (e) => <span className="whitespace-nowrap text-sage">{formatDateTime(e.ts)}</span>,
    },
    {
      id: 'actor',
      header: 'Who',
      cell: (e) => (
        <div className="min-w-0">
          <p className="truncate text-ink">
            {e.actor_name ?? (e.actor_id ? 'Unknown user' : 'System')}
          </p>
          {e.impersonated_by ? <Badge tone="watch">via operator</Badge> : null}
        </div>
      ),
    },
    {
      id: 'action',
      header: 'What',
      cell: (e) => (
        <div className="min-w-0">
          <p className="font-medium text-ink">
            {ACTION_LABELS[e.action] ?? e.action}
            {e.action === 'auth.denied' ? (
              <Badge tone="attention" className="ml-2 align-middle">
                denied
              </Badge>
            ) : null}
          </p>
          <p className="max-w-[28rem] truncate text-xs text-sage">{summary(e)}</p>
        </div>
      ),
    },
    {
      id: 'seq',
      header: '#',
      align: 'right',
      cell: (e) => <span className="font-mono text-xs text-sage">{e.seq}</span>,
    },
  ]

  return (
    <>
      <PageHeader
        eyebrow="Platform audit"
        title="Every admin action, on the record."
        description="Sign-ins, sign-outs, denials, impersonation and every admin and operator change, on the hash-chained platform log."
        actions={
          verify.data ? (
            verify.data.ok ? (
              <Badge tone="positive" icon={<ShieldCheck />}>
                Chain verified · {verify.data.count} events
              </Badge>
            ) : (
              <Badge tone="attention" icon={<ShieldAlert />}>
                Chain broken at #{verify.data.failed_seq}
              </Badge>
            )
          ) : null
        }
      />
      <div className="mb-5 grid gap-3 sm:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end">
        <Select
          label="Activity"
          name="action"
          value={action}
          placeholder="All activity"
          options={ACTION_FILTERS}
          onChange={(event) => {
            setAction(event.target.value)
            pager.reset()
          }}
        />
        <DateInput
          label="From"
          name="from"
          value={from}
          onChange={(event) => {
            setFrom(event.target.value)
            pager.reset()
          }}
        />
        <DateInput
          label="To"
          name="to"
          value={to}
          onChange={(event) => {
            setTo(event.target.value)
            pager.reset()
          }}
        />
        <Button
          variant="ghost"
          onClick={() => {
            setAction('')
            setFrom('')
            setTo('')
            pager.reset()
          }}
        >
          Clear
        </Button>
      </div>
      {audit.isError ? (
        isApiError(audit.error, 'mfa_required') ? (
          <MfaRequiredNotice onVerified={() => void audit.refetch()} />
        ) : (
          <Callout tone="danger" role="alert">
            {errorMessage(audit.error)}
          </Callout>
        )
      ) : (
        <DataTable
          caption="Platform audit events"
          columns={columns}
          rows={audit.data?.items ?? []}
          getRowId={(e) => `${e.chain}:${e.seq}`}
          loading={audit.isPending}
          onRowClick={(e) => setOpen(e)}
          empty={
            <p className="py-10 text-center text-sm text-sage">No events match these filters.</p>
          }
          pagination={{
            mode: 'server',
            hasPrevious: pager.hasPrevious,
            hasNext: Boolean(audit.data?.next_cursor),
            onPrevious: pager.previous,
            onNext: () => pager.next(audit.data?.next_cursor ?? null),
            summary: `Page ${pager.page}`,
          }}
        />
      )}
      <Drawer
        open={open !== null}
        onClose={() => setOpen(null)}
        title={open ? (ACTION_LABELS[open.action] ?? open.action) : 'Event'}
      >
        {open ? (
          <dl className="grid gap-4 px-5 py-5 text-sm">
            {(
              [
                ['When', formatDateTime(open.ts)],
                ['Who', open.actor_name ?? open.actor_id ?? 'System'],
                ['Started by operator', open.impersonated_by ?? '—'],
                ['Action', open.action],
                ['Record', open.record_id ? `${open.kind} · ${open.record_id}` : open.kind],
                ['Request', open.request_id ?? '—'],
                ['Sequence', `platform #${open.seq}`],
              ] as const
            ).map(([label, value]) => (
              <div key={label}>
                <dt className="text-xs font-semibold tracking-[0.06em] text-sage uppercase">
                  {label}
                </dt>
                <dd className="mt-1 break-all text-ink">{value}</dd>
              </div>
            ))}
            <div>
              <dt className="text-xs font-semibold tracking-[0.06em] text-sage uppercase">
                Details
              </dt>
              <dd className="mt-1">
                <pre className="overflow-x-auto rounded-lg border border-line bg-cream p-3 font-mono text-xs text-ink">
                  {JSON.stringify(open.details, null, 2)}
                </pre>
              </dd>
            </div>
            <div>
              <dt className="text-xs font-semibold tracking-[0.06em] text-sage uppercase">Hash</dt>
              <dd className="mt-1 break-all font-mono text-xs text-sage">{open.hash}</dd>
            </div>
          </dl>
        ) : null}
      </Drawer>
    </>
  )
}
